/**
 * S18 · Conflict merge — side-by-side local vs remote values with a
 * "keep local" / "keep remote" resolution. Resolving flips the task
 * back to `queued` with the decision baked into the payload.
 *
 * Deep-linkable from `/sync/conflicts/first` — a helper that routes to
 * the first conflict in the queue (used from the sync rollup banner).
 */
import { useEffect, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { SyncTask } from '@kapture/types';
import { Btn, Chip, GestureBar, I, Ico, ScreenFrame, StatusBar, T } from '@/components/primitives';
import { useAllSyncTasks, useResolveConflictMutation, useSyncTask } from '@kapture/client-core';

export const Route = createFileRoute('/sync/conflicts/$taskId')({
  component: ConflictPage,
  staticData: { mobile: true },
});

interface ConflictPayload {
  fieldId?: string;
  local?: { value: unknown; updatedAt?: string; editor?: string };
  remote?: { value: unknown; updatedAt?: string; editor?: string };
}

function ConflictPage() {
  const { taskId } = Route.useParams();
  const navigate = useNavigate();
  const { data: allTasks } = useAllSyncTasks();
  const { data: directTask } = useSyncTask(taskId === 'first' ? undefined : taskId);
  const resolve = useResolveConflictMutation();
  const [pendingChoice, setPendingChoice] = useState<'keep-local' | 'keep-remote' | null>(null);

  const task: SyncTask | null = (() => {
    if (taskId === 'first') {
      return (allTasks ?? []).find((t) => t.status === 'conflict') ?? null;
    }
    return directTask ?? null;
  })();

  // Redirect /first to the canonical URL once we've resolved it.
  useEffect(() => {
    if (taskId === 'first' && task) {
      navigate({
        to: '/sync/conflicts/$taskId',
        params: { taskId: task.id },
        replace: true,
      });
    }
  }, [taskId, task, navigate]);

  if (!task) {
    return (
      <ScreenFrame tone="light">
        <StatusBar />
        <Header />
        <div
          style={{
            flex: 1,
            display: 'grid',
            placeItems: 'center',
            padding: 24,
            color: T.textDim,
            fontFamily: T.font,
            fontSize: 13,
            lineHeight: 1.5,
            textAlign: 'center',
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 6 }}>
              Nothing to merge
            </div>
            No conflict tasks in the queue right now.{' '}
            <Link to="/sync" style={{ color: T.brandText, fontWeight: 600 }}>
              Back to queue
            </Link>
          </div>
        </div>
        <GestureBar />
      </ScreenFrame>
    );
  }

  const payload = task.payload as ConflictPayload;

  async function choose(resolution: 'keep-local' | 'keep-remote') {
    setPendingChoice(resolution);
    await resolve.mutateAsync({ id: task!.id, resolution });
    navigate({ to: '/sync' });
  }

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <Header />

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div
          style={{
            padding: 12,
            borderRadius: 12,
            background: T.warnSoft,
            border: `1px solid ${T.warn}`,
            color: T.warnText,
            display: 'flex',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <Ico d={I.warn} size={16} stroke={T.warnText} />
          <div style={{ flex: 1, fontSize: 12, fontFamily: T.font }}>
            <b style={{ fontWeight: 700 }}>Version conflict</b>
            <div style={{ opacity: 0.85, marginTop: 2 }}>
              {task.lastError ??
                'The server has a different value. Pick which one to keep — the other is archived.'}
            </div>
          </div>
        </div>

        <SideBySide
          tone="brand"
          title="Your edit"
          byline={payload.local?.editor ?? 'You'}
          at={payload.local?.updatedAt}
          value={payload.local?.value}
          current
        />
        <SideBySide
          tone="ink"
          title="Their edit"
          byline={payload.remote?.editor ?? 'Teammate'}
          at={payload.remote?.updatedAt}
          value={payload.remote?.value}
        />
      </div>

      <div style={{ flex: 1 }} />

      <div
        style={{
          padding: '10px 16px 12px',
          borderTop: `1px solid ${T.stroke}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <Btn
          tone="primary"
          size="md"
          full
          iconRight={<Ico d={I.arrow} size={16} />}
          disabled={resolve.isPending}
          onClick={() => void choose('keep-remote')}
          style={{ opacity: resolve.isPending ? 0.5 : 1 }}
        >
          {resolve.isPending && pendingChoice === 'keep-remote'
            ? 'Merging…'
            : `Use their edit${payload.remote?.value ? ` (${String(payload.remote.value)})` : ''}`}
        </Btn>
        <Btn
          tone="light"
          size="md"
          full
          onClick={() => void choose('keep-local')}
          disabled={resolve.isPending}
          style={{ opacity: resolve.isPending ? 0.5 : 1 }}
        >
          {resolve.isPending && pendingChoice === 'keep-local' ? 'Merging…' : 'Keep mine'}
        </Btn>
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}

function Header() {
  return (
    <div
      style={{
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        borderBottom: `1px solid ${T.stroke}`,
      }}
    >
      <Link
        to="/sync"
        aria-label="Back"
        style={{ color: T.text, display: 'flex', textDecoration: 'none' }}
      >
        <Ico d={I.back} size={20} />
      </Link>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 700, fontFamily: T.font }}>Resolve conflict</div>
        <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.font }}>
          Two versions exist — pick the canonical one
        </div>
      </div>
    </div>
  );
}

function SideBySide({
  tone,
  title,
  byline,
  at,
  value,
  current,
}: {
  tone: 'brand' | 'ink';
  title: string;
  byline: string;
  at?: string;
  value: unknown;
  current?: boolean;
}) {
  const isBrand = tone === 'brand';
  return (
    <div
      style={{
        padding: 16,
        borderRadius: 14,
        background: isBrand ? T.brandSoft : T.surface,
        border: `1px solid ${isBrand ? T.brand : T.stroke}`,
        boxShadow: isBrand ? '0 0 0 3px rgba(255,90,31,0.08)' : 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontFamily: T.mono,
            fontWeight: 600,
            color: isBrand ? T.brandInk : T.textDim,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {title}
        </div>
        {current && (
          <Chip tone="brand" size="sm">
            Current
          </Chip>
        )}
      </div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
          fontFamily: T.font,
          color: isBrand ? T.brandDark : T.text,
          letterSpacing: '-0.02em',
          wordBreak: 'break-word',
        }}
      >
        {stringifyValue(value)}
      </div>
      <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.font, marginTop: 8 }}>
        by <b style={{ fontWeight: 600, color: T.text }}>{byline}</b>
        {at
          ? ` · ${new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
          : ''}
      </div>
    </div>
  );
}

function stringifyValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return '(unserialisable)';
  }
}
